import React from "react";
import { Shield, Lock, Eye, AlertCircle, FileText, ClipboardCheck } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="bg-white py-16 mt-20">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-green-700 mb-4">Privacy Policy</h1>
          <div className="flex justify-center">
            <Shield className="w-12 h-12 text-green-600" />
          </div>
          <p className="text-gray-600 mt-4">
            Last Updated: April 2, 2025
          </p>
        </div>

        <div className="space-y-8">
          {/* Introduction */}
          <section className="bg-green-50 p-6 rounded-lg">
            <div className="flex items-start mb-4">
              <FileText className="w-6 h-6 text-green-700 mr-3 mt-1" />
              <h2 className="text-2xl font-semibold text-green-700">Introduction</h2>
            </div>
            <p className="text-gray-700">
              At GREEN ECO, we respect your privacy and are committed to protecting your personal data. 
              This privacy policy will inform you about how we look after your personal data when you 
              visit our website and tell you about your privacy rights and how the law protects you.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <div className="flex items-start mb-4">
              <ClipboardCheck className="w-6 h-6 text-green-700 mr-3 mt-1" />
              <h2 className="text-2xl font-semibold text-green-700">Information We Collect</h2>
            </div>
            <div className="ml-9">
              <p className="text-gray-700 mb-4">
                We may collect, use, store and transfer different kinds of personal data about you which we have grouped together as follows:
              </p>
              <ul className="list-disc ml-6 space-y-2 text-gray-700">
                <li><span className="font-medium">Identity Data</span> - includes first name, last name, username or similar identifier.</li>
                <li><span className="font-medium">Contact Data</span> - includes billing address, delivery address, email address and telephone numbers.</li>
                <li><span className="font-medium">Financial Data</span> - includes payment card details (processed securely through our payment processors).</li>
                <li><span className="font-medium">Transaction Data</span> - includes details about payments to and from you and other details of products you have purchased.</li>
                <li><span className="font-medium">Technical Data</span> - includes internet protocol (IP) address, browser type and version, time zone setting and location, browser plug-in types and versions, operating system and platform.</li>
                <li><span className="font-medium">Usage Data</span> - includes information about how you use our website and products.</li>
              </ul>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-green-50 p-6 rounded-lg">
            <div className="flex items-start mb-4">
              <Lock className="w-6 h-6 text-green-700 mr-3 mt-1" />
              <h2 className="text-2xl font-semibold text-green-700">How We Use Your Information</h2>
            </div>
            <div className="ml-9">
              <p className="text-gray-700 mb-4">
                We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances:
              </p>
              <ul className="list-disc ml-6 space-y-2 text-gray-700">
                <li>To process and deliver your order.</li>
                <li>To manage our relationship with you including notifying you about changes to our terms or privacy policy.</li>
                <li>To administer and protect our business and this website.</li>
                <li>To deliver relevant website content and advertisements to you and measure or understand the effectiveness of the advertising we serve to you.</li>
                <li>To use data analytics to improve our website, products/services, marketing, customer relationships and experiences.</li>
              </ul>
            </div>
          </section>

          {/* Data Security */}
          <section>
            <div className="flex items-start mb-4">
              <Eye className="w-6 h-6 text-green-700 mr-3 mt-1" />
              <h2 className="text-2xl font-semibold text-green-700">Data Security</h2>
            </div>
            <p className="text-gray-700 ml-9">
              We have put in place appropriate security measures to prevent your personal data from being accidentally lost, used or accessed in an unauthorized way, altered or disclosed. In addition, we limit access to your personal data to those employees, agents, contractors and other third parties who have a business need to know. They will only process your personal data on our instructions and they are subject to a duty of confidentiality.
            </p>
          </section>

          {/* Your Rights */}
          <section className="bg-green-50 p-6 rounded-lg">
            <div className="flex items-start mb-4">
              <AlertCircle className="w-6 h-6 text-green-700 mr-3 mt-1" />
              <h2 className="text-2xl font-semibold text-green-700">Your Rights</h2>
            </div>
            <div className="ml-9">
              <p className="text-gray-700 mb-4">
                Under certain circumstances, you have rights under data protection laws in relation to your personal data, including the right to:
              </p>
              <ul className="list-disc ml-6 space-y-2 text-gray-700">
                <li>Request access to your personal data.</li>
                <li>Request correction of your personal data.</li>
                <li>Request erasure of your personal data.</li>
                <li>Object to processing of your personal data.</li>
                <li>Request restriction of processing your personal data.</li>
                <li>Request transfer of your personal data.</li>
                <li>Right to withdraw consent.</li>
              </ul>
              <p className="text-gray-700 mt-4">
                If you wish to exercise any of the rights set out above, please contact us at <span className="text-green-700 font-medium">Info@greenecobagsusa.com</span>.
              </p>
            </div>
          </section>

          {/* Cookie Policy */}
          <section>
            <div className="flex items-start mb-4">
              <FileText className="w-6 h-6 text-green-700 mr-3 mt-1" />
              <h2 className="text-2xl font-semibold text-green-700">Cookie Policy</h2>
            </div>
            <p className="text-gray-700 ml-9">
              We use cookies to distinguish you from other users of our website, which helps us to provide you with a good experience when you browse our website and also allows us to improve our site. You can set your browser to refuse all or some browser cookies, or to alert you when websites set or access cookies.
            </p>
          </section>

          {/* Contact Us */}
          <section className="bg-green-50 p-6 rounded-lg">
            <div className="flex items-start mb-4">
              <Lock className="w-6 h-6 text-green-700 mr-3 mt-1" />
              <h2 className="text-2xl font-semibold text-green-700">Contact Us</h2>
            </div>
            <p className="text-gray-700 ml-9">
              If you have any questions about this privacy policy or our privacy practices, please contact us at <span className="text-green-700 font-medium">Info@greenecobagsusa.com</span> or by mail at 1905 Sherman St Suite 200, Denver, CO 80203, United States.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;